const normalizeFiniteNumber = value => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : undefined
}

const normalizePositiveWholeNumber = value => {
    const numericValue = normalizeFiniteNumber(value)
    return numericValue !== undefined && numericValue > 0 ? Math.floor(numericValue) : undefined
}

const normalizeNonNegativeWholeNumber = value => {
    const numericValue = normalizeFiniteNumber(value)
    return numericValue !== undefined && numericValue >= 0 ? Math.floor(numericValue) : undefined
}

const normalizeMasterBlacksmithRuntimeStatus = value => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined

    const offers = (Array.isArray(value.offers) ? value.offers : []).slice(0, 250).map(offer => {
        const packageID = normalizePositiveWholeNumber(offer?.packageID)
        if (packageID === undefined)
            return undefined
        const stockRemaining = normalizeNonNegativeWholeNumber(offer?.stockRemaining)
        const purchasedAmount = normalizeNonNegativeWholeNumber(offer?.purchasedAmount)
        return {
            packageID,
            ...(stockRemaining !== undefined ? { stockRemaining } : {}),
            ...(purchasedAmount !== undefined ? { purchasedAmount } : {}),
            ...(offer?.inactive === true ? { inactive: true } : {})
        }
    }).filter(Boolean)
    const balances = Object.fromEntries(Object.entries(value.balances && typeof value.balances === 'object' ? value.balances : {})
        .map(([key, amount]) => [String(key).slice(0, 50), normalizeNonNegativeWholeNumber(amount)])
        .filter(([key, amount]) => key && amount !== undefined)
        .slice(0, 100))
    const eventEndsAt = normalizePositiveWholeNumber(value.eventEndsAt)
    const updatedAt = normalizePositiveWholeNumber(value.updatedAt)
    const message = String(value.message ?? '').trim().slice(0, 500)
    const normalized = {
        ...(typeof value.active === 'boolean' ? { active: value.active } : {}),
        ...(eventEndsAt !== undefined ? { eventEndsAt } : {}),
        ...(updatedAt !== undefined ? { updatedAt } : {}),
        ...(offers.length > 0 ? { offers } : {}),
        ...(Object.keys(balances).length > 0 ? { balances } : {}),
        ...(message ? { message } : {})
    }
    return Object.keys(normalized).length > 0 ? normalized : undefined
}

export const normalizeCachedDashboardStatus = value => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined

    const normalizeLeaderboardEntries = entries =>
        (Array.isArray(entries) ? entries : [])
            .map(entry => {
                const rank = normalizePositiveWholeNumber(entry?.rank)
                const points = normalizeNonNegativeWholeNumber(entry?.points)
                const playerName = String(entry?.playerName ?? '').trim()
                const allianceName = String(entry?.allianceName ?? '').trim()
                const ownerID = normalizePositiveWholeNumber(entry?.ownerID)
                const level = normalizePositiveWholeNumber(entry?.level)
                const legendaryLevel = normalizeNonNegativeWholeNumber(entry?.legendaryLevel)

                if (rank === undefined || !playerName)
                    return undefined

                return {
                    rank,
                    ...(points !== undefined ? { points } : {}),
                    playerName,
                    ...(allianceName ? { allianceName } : {}),
                    ...(ownerID !== undefined ? { ownerID } : {}),
                    ...(level !== undefined ? { level } : {}),
                    ...(legendaryLevel !== undefined ? { legendaryLevel } : {})
                }
            })
            .filter(Boolean)
            .slice(0, 10)

    const allianceName = String(value?.allianceName ?? '').trim()
    const runningSince = normalizePositiveWholeNumber(value?.runningSince)
    const startTime = normalizePositiveWholeNumber(value?.startTime)
    const mightPoints = normalizePositiveWholeNumber(value?.mightPoints)
    const berriWorldRank = normalizePositiveWholeNumber(value?.berriWorldRank)
    const berriWorldPoints = normalizeNonNegativeWholeNumber(value?.berriWorldPoints)
    const berriWorldLootID = normalizePositiveWholeNumber(value?.berriWorldLootID)
    const berriWorldLeaderboard = normalizeLeaderboardEntries(value?.berriWorldLeaderboard)
    const berriWorldLeaderboardSource = String(value?.berriWorldLeaderboardSource ?? '').trim()
    const berriWorldLeaderboardUpdatedAt = normalizePositiveWholeNumber(value?.berriWorldLeaderboardUpdatedAt)
    const incomingKhanTauntCount = normalizeNonNegativeWholeNumber(value?.incomingKhanTauntCount)
    const recentKhanAttackCount = normalizeNonNegativeWholeNumber(value?.recentKhanAttackCount)
    const lastKhanAttackAt = normalizePositiveWholeNumber(value?.lastKhanAttackAt)
    const khanDefenceToolsCheckedAt = normalizePositiveWholeNumber(value?.khanDefenceToolsCheckedAt)
    const khanDefenceToolsEquippedCount = normalizeNonNegativeWholeNumber(value?.khanDefenceToolsEquippedCount)
    const khanDefenceToolsTargetCount = normalizeNonNegativeWholeNumber(value?.khanDefenceToolsTargetCount)
    const khanDefenceToolsMissingCount = normalizeNonNegativeWholeNumber(value?.khanDefenceToolsMissingCount)
    const khanDefenceToolsPendingHits = normalizeNonNegativeWholeNumber(value?.khanDefenceToolsPendingHits)
    const khanDefenceToolsRefillThreshold = normalizePositiveWholeNumber(value?.khanDefenceToolsRefillThreshold)
    const khanDefenceToolsLastRefilledAt = normalizePositiveWholeNumber(value?.khanDefenceToolsLastRefilledAt)
    const khanDefenceToolsLastRefilledCount = normalizeNonNegativeWholeNumber(value?.khanDefenceToolsLastRefilledCount)
    const defensiveBattlesRead = normalizeNonNegativeWholeNumber(value?.defensiveBattlesRead)
    const defensiveTroopsLost = normalizeNonNegativeWholeNumber(value?.defensiveTroopsLost)
    const lastDefensiveTroopsLost = normalizeNonNegativeWholeNumber(value?.lastDefensiveTroopsLost)
    const lastDefensiveWounded = normalizeNonNegativeWholeNumber(value?.lastDefensiveWounded)
    const lastDefenseReportAt = normalizePositiveWholeNumber(value?.lastDefenseReportAt)
    const lastDefenseAttackerName = String(value?.lastDefenseAttackerName ?? '').trim()
    const lastSeenAt = normalizePositiveWholeNumber(value?.lastSeenAt)
    const lastSessionDurationMs = normalizeNonNegativeWholeNumber(value?.lastSessionDurationMs)
    const masterBlacksmith = normalizeMasterBlacksmithRuntimeStatus(value?.masterBlacksmith)

    const normalizedStatus = {
        ...(allianceName ? { allianceName } : {}),
        ...(runningSince !== undefined ? { runningSince } : {}),
        ...(startTime !== undefined ? { startTime } : {}),
        ...(mightPoints !== undefined ? { mightPoints } : {}),
        ...(berriWorldRank !== undefined ? { berriWorldRank } : {}),
        ...(berriWorldPoints !== undefined ? { berriWorldPoints } : {}),
        ...(berriWorldLootID !== undefined ? { berriWorldLootID } : {}),
        ...(berriWorldLeaderboard.length > 0 ? { berriWorldLeaderboard } : {}),
        ...(berriWorldLeaderboardSource ? { berriWorldLeaderboardSource } : {}),
        ...(berriWorldLeaderboardUpdatedAt !== undefined ? { berriWorldLeaderboardUpdatedAt } : {}),
        ...(incomingKhanTauntCount !== undefined ? { incomingKhanTauntCount } : {}),
        ...(recentKhanAttackCount !== undefined ? { recentKhanAttackCount } : {}),
        ...(lastKhanAttackAt !== undefined ? { lastKhanAttackAt } : {}),
        ...(khanDefenceToolsCheckedAt !== undefined ? { khanDefenceToolsCheckedAt } : {}),
        ...(khanDefenceToolsEquippedCount !== undefined ? { khanDefenceToolsEquippedCount } : {}),
        ...(khanDefenceToolsTargetCount !== undefined ? { khanDefenceToolsTargetCount } : {}),
        ...(khanDefenceToolsMissingCount !== undefined ? { khanDefenceToolsMissingCount } : {}),
        ...(typeof value?.khanDefenceToolsRefillComplete === 'boolean' ? {
            khanDefenceToolsRefillComplete: value.khanDefenceToolsRefillComplete
        } : {}),
        ...(khanDefenceToolsPendingHits !== undefined ? { khanDefenceToolsPendingHits } : {}),
        ...(khanDefenceToolsRefillThreshold !== undefined ? { khanDefenceToolsRefillThreshold } : {}),
        ...(khanDefenceToolsLastRefilledAt !== undefined ? { khanDefenceToolsLastRefilledAt } : {}),
        ...(khanDefenceToolsLastRefilledCount !== undefined ? { khanDefenceToolsLastRefilledCount } : {}),
        ...(defensiveBattlesRead !== undefined ? { defensiveBattlesRead } : {}),
        ...(defensiveTroopsLost !== undefined ? { defensiveTroopsLost } : {}),
        ...(lastDefensiveTroopsLost !== undefined ? { lastDefensiveTroopsLost } : {}),
        ...(lastDefensiveWounded !== undefined ? { lastDefensiveWounded } : {}),
        ...(lastDefenseReportAt !== undefined ? { lastDefenseReportAt } : {}),
        ...(typeof value?.lastDefenseWon === 'boolean' ? { lastDefenseWon: value.lastDefenseWon } : {}),
        ...(lastDefenseAttackerName ? { lastDefenseAttackerName } : {}),
        ...(lastSeenAt !== undefined ? { lastSeenAt } : {}),
        ...(lastSessionDurationMs !== undefined ? { lastSessionDurationMs } : {}),
        ...(masterBlacksmith ? { masterBlacksmith } : {})
    }

    return Object.keys(normalizedStatus).length > 0 ? normalizedStatus : undefined
}

export const buildCachedInitialRuntimeStatus = user => {
    const cachedStatus = {
        leagueDivisionRank: normalizePositiveWholeNumber(user?.cachedLeagueDivisionRank),
        leagueSeasonRank: normalizePositiveWholeNumber(user?.cachedLeagueSeasonRank),
        leagueDivisionId: normalizePositiveWholeNumber(user?.cachedLeagueDivisionId),
        leagueDivisionSize: normalizePositiveWholeNumber(user?.cachedLeagueDivisionSize)
    }

    if (Array.isArray(user?.cachedResourceAlerts))
        cachedStatus.resourceAlerts = user.cachedResourceAlerts

    const cachedDashboardStatus = normalizeCachedDashboardStatus(user?.cachedDashboardStatus)
    if (cachedDashboardStatus)
        Object.assign(cachedStatus, cachedDashboardStatus)

    return Object.entries(cachedStatus).some(([, value]) =>
        Array.isArray(value) ? value.length > 0 : value !== undefined) ? cachedStatus : undefined
}

export const resolveRuntimeUptimeMs = (status, now = Date.now()) => {
    const normalizedStartTime =
        normalizePositiveWholeNumber(status?.runningSince) ??
        normalizePositiveWholeNumber(status?.startTime)
    if (normalizedStartTime !== undefined) {
        const effectiveNow = normalizePositiveWholeNumber(now) ?? Date.now()
        return Math.max(0, effectiveNow - normalizedStartTime)
    }

    return normalizeNonNegativeWholeNumber(status?.lastSessionDurationMs) ?? 0
}
